import { useState } from 'react'
import { Spinner } from '@fluentui/react'
import api from '../api-client'
import { useUtmQuery } from '../hooks'
import {
  errorToString,
  dollars,
  dollarsToNum,
  Incentive,
  MAX_LOAN_THRESHOLD,
  Adders
} from '@oneethos/shared'
import { LoanSummary } from '../components'
import { useNavigate } from 'react-router-dom'
import { FaCheck } from "react-icons/fa6"

type ReferralError = {
  message: string
  link?: string
}

type ReferralErrorAlertProps = {
  error: ReferralError
}

const toSafeExternalLink = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  try {
    const url = new URL(value)
    return ['http:', 'https:'].includes(url.protocol) ? value : undefined
  } catch {
    return undefined
  }
}

const ReferralErrorAlert = ({ error }: ReferralErrorAlertProps) => (
  <div className="alert alert-danger">
    <div>{error.message}</div>
    <div className='mt-2'>{error.link ? (
      <>
        {' '}
        <a
          href={error.link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-underline"
        >
          {error.link}
        </a>
      </>
    ) : null}
    </div>
  </div>
)

type SendReferralLinkProps = {
  formData: any
  isResidential: boolean
  isCommercial: boolean
  incentive: Incentive
}

export const SendReferralLink = ({
  formData,
  isResidential,
  isCommercial,
  incentive,
}: SendReferralLinkProps) => {
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<ReferralError | null>(null)

  const utm = useUtmQuery()
  const navigate = useNavigate()

  const { loanAmount } = formData

  const submitReferral = () => {
    setError(null)


    const adders = new Adders(formData.adders).filterNonZero()

    setSending(true)
    const payload: any = {
      ...utm,
      projectType: formData.projectType,
      email,
      businessName: formData.businessName || '',
      incentive,
      installationAddress: formData.installationAddress,
      userAddress: formData.installationAddress,
      solarCost: dollarsToNum(formData.solarCost),
      systemSizeKW: formData.systemSizeKW ? parseFloat(formData.systemSizeKW) : undefined,
      storageCapacity: formData.storageCapacity ? parseFloat(formData.storageCapacity) : undefined,
      teslaPowerwallQty: formData.teslaPowerwallQty,
      storageQty: formData.storageQty,
      adders: adders.getList(),
      equipmentType: formData.equipmentType,
    }

    if (!isResidential) {
      payload.avgMonthlyUtilityBill = dollarsToNum(formData.avgMonthlyUtilityBill)
      payload.estAvgMonthlyUtilityBillAfterSolar = dollarsToNum(formData.estAvgMonthlyUtilityBillAfterSolar)
    }

    api.post('/loanapps', payload).then(res => {
      navigate(`/project/${res._id}`)
    }).catch(ex => {
      setSending(false)
      setError({
        message: errorToString(ex),
        link: toSafeExternalLink(ex?.link),
      })
    })
  }

  return <div>
    <div className="section-title">
      <h2>
        {isResidential ? 'Send Prequalification Link' : 'Send Link to Apply'}
      </h2>
    </div>
    <LoanSummary
      formData={formData}
      incentive={incentive}
    />
    <div className="w-form">
      <form id="email-form-2" name="email-form-2" data-name="Email Form 2" method="get">
        {!formData.equipmentType?.isTesla &&
          dollarsToNum(loanAmount) >= MAX_LOAN_THRESHOLD &&
          isResidential ?
          <div className="alert alert-info mt-3">
            Loans larger than {dollars(MAX_LOAN_THRESHOLD)} will
            require additional income verification.
          </div> : null
        }
        {isCommercial &&
          <div className='alert alert-warning my-3' role='alert'>
            <b>Before you send the referral, please consult your customer on the following points: <br /></b>
            <div className='mt-2'>
              <FaCheck className='me-1' />Does their business make a profit?<br />
            </div>
            <div className='mt-1 mb-2'>
              <FaCheck className='me-1' />Is the profit enough to pay all of their bills with money still left over?<br />
            </div>
            If the answer to either of these questions is no, consider that their chances of approval may not be high;
            however please do not discourage the applicant from applying and still allow them the opportunity to
            submit the application.
          </div>
        }
        <div className="form-group mt-4">
          <label className="form-label">Enter customer email address</label>
          <input type="email"
            className="form-control"
            maxLength={256}
            name="email"
            data-name="email"
            placeholder="client@email.com"
            id="email"
            autoComplete="off"
            value={email}
            onChange={ev => setEmail(ev.target.value)}
          />
        </div>
        <div className="form-group">
          By clicking "Send Link" I confirm that the customer has provided their explicit consent to receive email
          communications from Climate First Bank regarding this referral.
        </div>
        <div className="buttons form-group">
          {sending ? <Spinner /> : <button
            // disabled={!installWillCompleteIn30Days}
            type="button"
            className="btn btn-primary"
            onClick={submitReferral}
          >Send Link</button>}
        </div>
        {error ? <ReferralErrorAlert error={error} /> : null}
      </form>
    </div >
  </div >
}

export default SendReferralLink